import React, { useEffect, useState, useCallback } from "react";
import { CalendarCheck, Plus, CheckCircle2, XCircle, Clock } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { PageHeader, AppButton, AppCard, AppCardContent, AppModal, EmptyState } from "../ui";
import Shimmer from "../Common/Shimmer";
import CommonToaster from "../Common/CommonToaster";
import "./attendance.scss";

/**
 * StudentAttendancePage — the logged-in student's own attendance (school layer).
 * Per-batch % with eligibility, a recent P/A/L strip, and a leave-request flow.
 * Route: /s/attendance (gated by attendanceManagement).
 */
const CELL = {
  present: { ch: "P", cls: "att-cell--present" },
  partial: { ch: "P", cls: "att-cell--partial" },
  absent: { ch: "A", cls: "att-cell--absent" },
  excused: { ch: "L", cls: "att-cell--excused" },
  not_marked: { ch: "·", cls: "att-cell--notmarked" },
  upcoming: { ch: "", cls: "att-cell--upcoming" },
};

const pctClass = (p) => (p >= 75 ? "att-pct--good" : p >= 50 ? "att-pct--warn" : "att-pct--bad");
const fmtDate = (s) => (s ? new Date(`${s}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "-");

const LEAVE_BADGE = {
  pending: { cls: "attendance-badge--pending", icon: Clock, label: "Pending" },
  approved: { cls: "attendance-badge--done", icon: CheckCircle2, label: "Approved" },
  rejected: { cls: "attendance-badge--absent", icon: XCircle, label: "Rejected" },
};

const StudentAttendancePage = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [summary, setSummary] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Leave modal
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ fromDate: today, toDate: today, reason: "", batchId: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, lRes] = await Promise.all([
        apiClient.get("/attendance/my-summary"),
        apiClient.get("/attendance/my-leaves").catch(() => ({ data: { leaves: [] } })),
      ]);
      setSummary(sRes.data);
      setLeaves(lRes.data.leaves || []);
    } catch {
      setToast({ show: true, message: "Failed to load attendance.", type: "danger" });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitLeave = async () => {
    if (form.toDate < form.fromDate) {
      setToast({ show: true, message: "End date can't be before start date.", type: "danger" });
      return;
    }
    setSaving(true);
    try {
      await apiClient.post("/attendance/leave-request", {
        fromDate: form.fromDate,
        toDate: form.toDate,
        reason: form.reason,
        batchId: form.batchId || undefined,
      });
      setToast({ show: true, message: "Leave request submitted.", type: "success" });
      setModalOpen(false);
      setForm({ fromDate: today, toDate: today, reason: "", batchId: "" });
      load();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to submit leave.", type: "danger" });
    }
    setSaving(false);
  };

  const batches = summary?.batches || [];
  const overall = summary?.overall;

  return (
    <div className="attendance-page sl-page">
      <PageHeader
        icon={CalendarCheck}
        title="My Attendance"
        description="Your attendance across batches, with eligibility status"
        actions={
          <AppButton variant="primary" size="sm" onClick={() => setModalOpen(true)}>
            <Plus size={15} /> Apply for Leave
          </AppButton>
        }
      />

      {loading ? (
        <Shimmer height={70} count={4} />
      ) : batches.length === 0 ? (
        <EmptyState
          icon={CalendarCheck}
          title="No attendance yet"
          description="Your attendance will appear here once your classes begin."
        />
      ) : (
        <>
          {overall != null && (
            <div className="att-overall">
              <span className={`att-overall__pct ${pctClass(overall)}`}>{overall}%</span>
              <span className="att-overall__label">Overall attendance</span>
            </div>
          )}

          <div className="att-student-grid">
            {batches.map((b) => (
              <AppCard key={b.batchId} className="att-student-card">
                <AppCardContent>
                  <div className="att-student-card__head">
                    <span className="att-student-card__name">{b.batchName}</span>
                    {b.percentage == null ? (
                      <span className="att-pct att-pct--none">—</span>
                    ) : (
                      <span className={`att-pct ${pctClass(b.percentage)}`}>{b.percentage}%</span>
                    )}
                  </div>

                  {/* The bar carries the percentage's own colour class, so the
                      75/50 thresholds are defined once (pctClass) and read by
                      both the figure and the fill. */}
                  {b.percentage != null && (
                    <div className="att-progress">
                      <span
                        className={`att-progress__fill ${pctClass(b.percentage)}`}
                        style={{ width: `${Math.min(100, Math.max(0, b.percentage))}%` }}
                      />
                    </div>
                  )}

                  <div className="att-student-card__meta">
                    <span>{b.present} / {b.held} classes</span>
                    {b.eligible === false && <span className="att-ineligible">Below {b.minAttendancePct}%</span>}
                    {b.eligible === true && <span className="att-eligible">Eligible</span>}
                  </div>

                  {b.recent.length > 0 && (
                    <>
                      <span className="att-student-card__striplabel">
                        Last {b.recent.length} {b.recent.length === 1 ? "class" : "classes"}
                      </span>
                      <div className="att-strip">
                        {b.recent.map((c) => {
                          const cfg = CELL[c.status] || CELL.not_marked;
                          return (
                            <span key={c.date} className={`att-cell ${cfg.cls}`} title={`${c.date} — ${c.status}`}>
                              {cfg.ch}
                            </span>
                          );
                        })}
                      </div>
                    </>
                  )}
                </AppCardContent>
              </AppCard>
            ))}
          </div>

          {/* Leave history */}
          <h6 className="att-section-title">My Leave Requests</h6>
          {leaves.length === 0 ? (
            <p className="text-muted" style={{ fontSize: "0.85rem" }}>No leave requests yet.</p>
          ) : (
            <div className="att-leave-list">
              {leaves.map((lv) => {
                const badge = LEAVE_BADGE[lv.status] || LEAVE_BADGE.pending;
                const Icon = badge.icon;
                return (
                  <div key={lv._id} className="att-leave-row">
                    <div className="att-leave-row__dates">
                      {fmtDate(lv.fromDate)} – {fmtDate(lv.toDate)}
                      {lv.batch?.name && <span className="att-leave-row__batch">{lv.batch.name}</span>}
                    </div>
                    <div className="att-leave-row__reason">{lv.reason || "—"}</div>
                    <span className={`attendance-badge ${badge.cls}`}>
                      <Icon size={12} /> {badge.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <AppModal
        show={modalOpen}
        onHide={() => setModalOpen(false)}
        title="Apply for Leave"
        icon={<CalendarCheck size={18} />}
        size="sm"
        footer={
          <>
            <AppButton variant="outline" onClick={() => setModalOpen(false)} disabled={saving}>Cancel</AppButton>
            <AppButton variant="primary" onClick={submitLeave} disabled={saving}>
              {saving ? "Submitting…" : "Submit"}
            </AppButton>
          </>
        }
      >
        <div className="att-leave-form">
          <label>From
            <input type="date" className="form-control form-control-sm" value={form.fromDate}
              onChange={(e) => setForm((f) => ({ ...f, fromDate: e.target.value }))} />
          </label>
          <label>To
            <input type="date" className="form-control form-control-sm" value={form.toDate} min={form.fromDate}
              onChange={(e) => setForm((f) => ({ ...f, toDate: e.target.value }))} />
          </label>
          {batches.length > 1 && (
            <label>Batch (optional)
              <select className="form-select form-select-sm" value={form.batchId}
                onChange={(e) => setForm((f) => ({ ...f, batchId: e.target.value }))}>
                <option value="">All my batches</option>
                {batches.map((b) => <option key={b.batchId} value={b.batchId}>{b.batchName}</option>)}
              </select>
            </label>
          )}
          <label>Reason
            <textarea className="form-control form-control-sm" rows={2} value={form.reason}
              placeholder="Optional" onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} />
          </label>
        </div>
      </AppModal>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default StudentAttendancePage;
